import { useState, type SyntheticEvent } from 'react';
import type { MessageItem } from '../api/endpoints';
import { messageFor } from '../api/messages';
import { dateTime } from '../format';
import { OfflineReason, useOnlineAction } from './OnlineOnly';
import { Alert, Button } from './ui';

/**
 * Verlauf eines Fadens. Texte werden nur als Text angezeigt (`white-space: pre-wrap`), nie als HTML und ohne
 * automatische Links (Plan Nr. 40).
 */
export function MessageList({ messages }: { messages: readonly MessageItem[] }) {
  return (
    <ul className="stack message-list" aria-label="Verlauf">
      {messages.map((message) => (
        <li
          key={message.id}
          className={message.mine ? 'message message--mine' : 'message'}
          data-side={message.side}
        >
          <p className="muted message__meta">
            <strong>{message.mine ? 'Sie' : message.senderName}</strong> ·{' '}
            {dateTime(message.createdAt)}
          </p>
          {message.redacted ? (
            <p className="muted">
              <em>Nachricht entfernt</em>
            </p>
          ) : (
            <p className="message-text">{message.text}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Antwortfeld mit Zeichenzähler. `onSend` wirft bei Fehlern, die Meldung zeigt dieses Feld selbst. */
export function ReplyForm({
  maxLength,
  onSend,
}: {
  maxLength: number;
  onSend: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const online = useOnlineAction();
  const [error, setError] = useState<string | null>(null);

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    onSend(text).then(
      () => {
        setText('');
        setBusy(false);
      },
      (caught: unknown) => {
        setError(messageFor(caught));
        setBusy(false);
      },
    );
  };

  return (
    <form className="stack" onSubmit={submit}>
      {error !== null && <Alert kind="error">{error}</Alert>}
      <div className="field">
        <label htmlFor="antwort-text">Antwort</label>
        <textarea
          id="antwort-text"
          rows={4}
          maxLength={maxLength}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
        <span className="field__hint">
          {text.length} von {maxLength} Zeichen. Nur Text, keine Anhänge.
        </span>
      </div>
      <div>
        <Button
          icon="mail"
          type="submit"
          busy={busy}
          disabled={text.trim() === '' || online.disabled}
        >
          Antwort senden
        </Button>
        <OfflineReason keeps="Ihr Text bleibt stehen, bis Sie senden." />
      </div>
    </form>
  );
}
